import { cx } from '@bangle.io/base-utils';
import {
  type ColorThemePreference,
  colorThemeMode,
} from '@bangle.io/constants';
import { useCoreServices } from '@bangle.io/context';
import { useAtomValue, useSetAtom } from 'jotai';
import React from 'react';

/**
 * The reader only ever offers the two Sariz palettes, labelled by their
 * scheme ("Light" / "Dark") — readers have no reason to know the app's eight
 * named themes exist. It writes the app's own color-theme preference, so the
 * choice persists across reloads and stays consistent with the note editor
 * on the same browser. A browser that never chose a theme starts on Dark:
 * `WorkbenchStateService` seeds `READER_DEFAULT_COLOR_THEME` while the
 * reader route is loading.
 */
export function ReaderThemeToggle() {
  const { workbenchState } = useCoreServices();
  const preference = useAtomValue(workbenchState.$colorThemePreference);
  const setPreference = useSetAtom(workbenchState.$colorThemePreference);
  const activeMode = colorThemeMode(preference);

  const options: {
    label: string;
    mode: 'light' | 'dark';
    theme: ColorThemePreference;
  }[] = [
    {
      label: t.app.settings.general.colorThemeLight,
      mode: 'light',
      theme: 'sarizlight',
    },
    {
      label: t.app.settings.general.colorThemeDark,
      mode: 'dark',
      theme: 'sariz',
    },
  ];

  return (
    <div
      role="group"
      aria-label={t.app.settings.general.colorThemeLabel}
      className="sn-reader-theme-toggle"
    >
      {options.map((option) => {
        const selected = option.mode === activeMode;

        return (
          <button
            key={option.theme}
            type="button"
            aria-pressed={selected}
            onClick={() => setPreference(option.theme)}
            className={cx(
              'rounded-md px-2 py-1 font-medium transition-colors',
              selected
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
